import { configureStore } from "@reduxjs/toolkit";
import { enableMapSet, setAutoFreeze } from "immer";
import todoReducer from "./reducers/test";
import chatReducer from "./reducers/chatReducer";
import authReducer from "./reducers/authReducer";
import toastReducer from "./reducers/toastReducer";
import patientReducer from "./reducers/patientReducer";
import doctorReducer from "./reducers/doctorReducer";

// Enable Map/Set support in Immer to handle Twilio objects with Maps
enableMapSet();
// Disable auto-freeze to prevent Immer from freezing objects with Maps
setAutoFreeze(false);

export const store = configureStore({
  reducer: {
    todoReducer,
    chatReducer,
    authReducer,
    toastReducer,
    patientReducer,
    doctorReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore these action types that contain Twilio objects
        ignoredActions: [
          'chat/saveActiveChannelList',
          'chat/channelToBeLoaded',
          'chat/saveMessageHistory',
          'chat/saveNewMessage',
          'chat/removeMessage',
          'chat/saveReadMessage',
          'chat/saveChatChannel',
          'chat/saveReArrangeChannelList',
          'chat/saveNewChannel',
          'chat/saveChannelUpdates',
        ],
        // Ignore these field paths in all actions
        ignoredActionPaths: ['payload.channel', 'payload.conversation', 'payload._internalState'],
        // Ignore these paths in the state
        ignoredPaths: [
          'chatReducer.activeChannelList',
          'chatReducer.channelList',
          'chatReducer.connectedChannel',
          'chatReducer.connectedChannelMessage',
          'chatReducer.lastChannelMessage',
          'chatReducer.lastChannelReadMessage',
        ],
      },
      immutableCheck: {
        // Completely ignore immutability check for chat reducer paths
        ignoredPaths: [
          'chatReducer.activeChannelList',
          'chatReducer.channelList',
          'chatReducer.connectedChannel',
          'chatReducer.connectedChannelMessage',
          'chatReducer.lastChannelMessage',
          'chatReducer.lastChannelReadMessage',
        ],
      },
      thunk: {
        extraArgument: undefined,
      },
    }),
  devTools: {
    // Disable state serialization in DevTools for Twilio objects
    serialize: false,
    stateSanitizer: (state: any) => {
      // Sanitize Twilio objects for DevTools
      if (state.chatReducer) {
        return {
          ...state,
          chatReducer: {
            ...state.chatReducer,
            activeChannelList: '<<TWILIO_CHANNELS>>',
            channelList: state.chatReducer.channelList?.length ? `<<${state.chatReducer.channelList.length} CHANNELS>>` : [],
            connectedChannel: state.chatReducer.connectedChannel ? '<<CONNECTED_CHANNEL>>' : null,
          },
        };
      }
      return state;
    },
  },
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
